import { Action, Selector, State, StateContext } from "@ngxs/store";
import { IRole } from "../../interfaces/roles.interface";
import { RolesService } from "../../services/roles/roles.service";
import { Injectable } from "@angular/core";
import { RoleActions } from "./role.action";
import { catchError, tap, throwError } from "rxjs";
import { Pagination } from "../../interfaces/deliveries.interface";

export class RoleStateModel {
  role: IRole | null = null;
  roles: Pagination<IRole> = { items: [], page: 1, pageSize: 10, total: 0 };
  rolesSearchQuery: string = '';
  superAdminPermissions: string[] = [];
  adminPermissions: string[] = [];
  permissionModulesLabels: any = {};
  modulesPermissions: any = {};
  platformPermissions: any = {};
  loading: boolean = false;
  editingRoleLoading: boolean = false;
  error: any | null = null;
}

@State<RoleStateModel>({
  name: 'role',
  defaults: {
    role: null,
    roles: { items: [], page: 1, pageSize: 10, total: 0 },
    rolesSearchQuery: '',
    superAdminPermissions: [],
    adminPermissions: [],
    modulesPermissions: {},
    permissionModulesLabels: {},
    platformPermissions: {},
    loading: false,
    editingRoleLoading: false,
    error: null,
  },
})

@Injectable()
export class RoleState {
  constructor(private rolesService: RolesService) {

  }

  @Selector()
  static getRoles(state: RoleStateModel): Pagination<IRole> {
    return state.roles;
  }

  @Selector()
  static getPermissions(state: RoleStateModel): string[] {
    return state.roles.items.flatMap((role) => role.permissions);
  }

  @Selector()
  static getRole(state: RoleStateModel): IRole | null {
    return state.role;
  }

  @Selector()
  static getLoading(state: RoleStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static isEditingRoleLoading(state: RoleStateModel): boolean {
    return state.editingRoleLoading;
  }

  @Selector()
  static getError(state: RoleStateModel): any | null {
    return state.error;
  }

  @Selector()
  static getSuperAdminPermissions(state: RoleStateModel): string[] {
    return state.superAdminPermissions;
  }

  @Selector()
  static getAdminPermissions(state: RoleStateModel): string[] {
    return state.adminPermissions;
  }

  @Selector()
  static getPermissionModulesLabels(state: RoleStateModel): any {
    return state.permissionModulesLabels;
  }

  @Selector()
  static getModulesPermissions(state: RoleStateModel): any {
    return state.modulesPermissions;
  }

  @Selector()
  static getPlatformPermissions(state: RoleStateModel): any {
    return state.platformPermissions;
  }


  @Action(RoleActions.GetRoles)
  getRoles(ctx: StateContext<RoleStateModel>, action: RoleActions.GetRoles) {
    ctx.patchState({ loading: true, error: null });
    return this.rolesService.getRoles(action.page, action.pageSize, action.q).pipe(
      tap((response) => {
        ctx.patchState({ roles: response.data, rolesSearchQuery: action.q ?? '', loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }

  @Action(RoleActions.GetRolesSuperAdmin)
  getRolesSuperAdmin(ctx: StateContext<RoleStateModel>, action: RoleActions.GetRolesSuperAdmin) {
    ctx.patchState({ loading: true, error: null });
    return this.rolesService.getRolesSuperAdmin(action.page, action.pageSize).pipe(
      tap((response) => {
        ctx.patchState({ roles: response.data, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }

  @Action(RoleActions.CreateRole)
  createRole(ctx: StateContext<RoleStateModel>, action: RoleActions.CreateRole) {
    ctx.patchState({ loading: true, error: null });
    return this.rolesService.createRole(action.role).pipe(
      tap((response) => {
        const { page, pageSize } = ctx.getState().roles;
        const q = ctx.getState().rolesSearchQuery;
        ctx.dispatch(new RoleActions.GetRoles(page, pageSize, q || undefined));
        ctx.patchState({ role: response.data, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }

  @Action(RoleActions.UpdateRole)
  updateRole(ctx: StateContext<RoleStateModel>, action: RoleActions.UpdateRole) {
    ctx.patchState({ loading: true, error: null });
    return this.rolesService.updateRole(action.id, action.role).pipe(
      tap((response) => {
        const { page, pageSize } = ctx.getState().roles;
        const q = ctx.getState().rolesSearchQuery;
        ctx.dispatch(new RoleActions.GetRoles(page, pageSize, q || undefined));
        ctx.patchState({ role: response.data, loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }

  @Action(RoleActions.DeleteRole)
  deleteRole(ctx: StateContext<RoleStateModel>, action: RoleActions.DeleteRole) {
    ctx.patchState({ loading: true, error: null });
    return this.rolesService.deleteRole(action.id).pipe(
      tap((response) => {
        const { page, pageSize } = ctx.getState().roles;
        const q = ctx.getState().rolesSearchQuery;
        ctx.dispatch(new RoleActions.GetRoles(page, pageSize, q || undefined));
        ctx.patchState({ loading: false });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }

  @Action(RoleActions.GetRoleById)
  getRoleById(ctx: StateContext<RoleStateModel>, action: RoleActions.GetRoleById) {
    ctx.patchState({ editingRoleLoading: true, error: null });
    return this.rolesService.getRoleById(action.id).pipe(
      tap((response) => {
        ctx.patchState({ role: response.data, editingRoleLoading: false });
      }),
      catchError((error) => {
        ctx.patchState({ editingRoleLoading: false, error: error.error?.message });
        return throwError(() => error);
      })
    );
  }

  @Action(RoleActions.GetSuperAdminPermissions)
  getSuperAdminPermissions(ctx: StateContext<RoleStateModel>) {
    ctx.patchState({ loading: true, error: null });
    const permissions :string[] = this.rolesService.getSuperAdminPermissions();
    ctx.patchState({ superAdminPermissions: permissions, loading: false });
    return permissions;
  }

  @Action(RoleActions.GetAdminPermissions)
  getAdminPermissions(ctx: StateContext<RoleStateModel>) {
    ctx.patchState({ loading: true, error: null });
    const permissions = this.rolesService.getAdminPermissions();
    ctx.patchState({ adminPermissions: permissions, loading: false });
  }


  @Action(RoleActions.GetPermissionsModules)
  getPermissionsModules(ctx: StateContext<RoleStateModel>) {
    const modules = this.rolesService.getModulesPermissions();
    ctx.patchState({ modulesPermissions: modules, loading: false });
  }

  @Action(RoleActions.GetPermissionModulesLabels)
  getPermissionModulesLabels(ctx: StateContext<RoleStateModel>) {
    const labels = this.rolesService.getPermissionModulesLabels();
    ctx.patchState({ permissionModulesLabels: labels, loading: false });
  }

  @Action(RoleActions.GetPlatformPermissions)
  getPlatformPermissions(ctx: StateContext<RoleStateModel>) {
    const platforms = this.rolesService.getPlatformPermissions();
    ctx.patchState({ platformPermissions: platforms });
  }

}
